/**
 * Чтение Criteria Document: чистые функции без React (FE-071, FE-073).
 *
 * Здесь нет ни одного расчёта модели подбора — веса, readiness и допуск считает
 * backend (CLAUDE.md §3 п. 4). Функции ниже только раскладывают уже посчитанный
 * документ по трём меткам интерфейса — **обязательно / желательно / без
 * предпочтений** (MVP_PRD §11) — и форматируют числа.
 *
 * Ключевое различие, которое нельзя терять: «вопрос ещё не задавали» и
 * «пользователю всё равно» — разные состояния. Первое показывается как пустая
 * строка критерия, второе — как явное «без предпочтений».
 */

import type {
  CriteriaBudget,
  CriteriaFeatures,
  CriteriaFuel,
  CriteriaGeography,
  CriteriaMakes,
  CriteriaModels,
  CriteriaPayload,
  CriteriaThresholds,
  CriteriaWish,
  Demotion,
  EmphasisLevel,
  ModelRef,
  PowertrainMode,
  WeightPreview,
} from './types';

/** Метка критерия на экранах критериев (MVP_PRD §11). */
export type CriterionStrength = 'required' | 'preferred' | 'none' | 'unset';

/** Порог, ниже которого блок «Что для вас важнее» не показывается. */
const NEUTRAL_LEVEL: EmphasisLevel = 'normal';

// --- Метки критериев --------------------------------------------------------------------

export function bodyTypesStrength(payload: CriteriaPayload): CriterionStrength {
  const { values, mode } = payload.body_types;
  if (mode === 'required' && values.length > 0) return 'required';
  if (mode === 'any') return 'none';
  return 'unset';
}

export function maxAgeStrength(payload: CriteriaPayload): CriterionStrength {
  if (payload.max_age.years !== null) return 'required';
  if (payload.max_age.no_limit) return 'none';
  return 'unset';
}

/**
 * Топливо. Перечисленный список допустимого и любое исключение — hard-фильтр;
 * `preferred` («лучше дизель, но бензин тоже подойдёт») влияет только на score.
 */
export function fuelStrength(fuel: CriteriaFuel): CriterionStrength {
  if (fuel.allowed.length > 0 || fuel.excluded.length > 0) return 'required';
  if (fuel.preferred.length > 0) return 'preferred';
  return 'unset';
}

/** Коробка и привод: жёсткость приходит готовой, `unset` — вопрос не задавался. */
export function powertrainStrength(mode: PowertrainMode): CriterionStrength {
  if (mode === 'required') return 'required';
  if (mode === 'preferred') return 'preferred';
  if (mode === 'any') return 'none';
  return 'unset';
}

export function featuresStrength(features: CriteriaFeatures): CriterionStrength {
  if (features.required.length > 0) return 'required';
  if (features.preferred.length > 0) return 'preferred';
  return features.specified ? 'none' : 'unset';
}

export function usageStrength(payload: CriteriaPayload): CriterionStrength {
  return payload.usage.length > 0 ? 'preferred' : 'unset';
}

export function prioritiesStrength(payload: CriteriaPayload): CriterionStrength {
  return payload.priorities.length > 0 ? 'preferred' : 'unset';
}

/**
 * Марки. Обязательная («ищу только BMW») и исключённая («только не Renault») —
 * hard-критерии, предпочтительная — мягкий признак, который **не даёт баллов**
 * (MATCHING §5.4). Метка обязана это различать.
 */
export function makesStrength(makes: CriteriaMakes): CriterionStrength {
  if (makes.required.length > 0 || makes.excluded.length > 0) return 'required';
  if (makes.preferred.length > 0) return 'preferred';
  return 'unset';
}

/**
 * География. `required` — единственный случай, когда она исключает автомобили
 * («только в моей земле»); во всех остальных она лишь задаёт порядок набора и
 * потому помечается как предпочтение, а не как условие (MVP_PRD §43.3).
 */
export function geographyStrength(geography: CriteriaGeography): CriterionStrength {
  if (geography.mode === 'required') return 'required';
  if (geography.home_state_code !== null || geography.state_codes.length > 0) return 'preferred';
  // Явное «вся Германия»: вопрос задан и закрыт — это не пустая строка.
  return geography.specified ? 'none' : 'unset';
}

/**
 * Есть ли что показывать в блоке «Где искать».
 *
 * Пустая плашка «не указано» не создаётся: секция скрывается целиком, если
 * пользователь ничего о географии не сказал (FE-350).
 */
export function hasGeography(geography: CriteriaGeography): boolean {
  return geography.home_state_code !== null || geography.home_city !== null;
}

export function hasMakes(makes: CriteriaMakes): boolean {
  return makes.required.length > 0 || makes.preferred.length > 0 || makes.excluded.length > 0;
}

// --- Модели и пожелания (v4, KARL_DIALOG_RFC §6.1–6.2) ------------------------------------

/**
 * Модели. Различие то же, что у марок, и терять его нельзя: «только Golf» —
 * hard-правило `MODEL_REQUIRED`, «Golf нравится» — предпочтение без баллов.
 */
export function modelsStrength(models: CriteriaModels): CriterionStrength {
  if (models.required.length > 0 || models.excluded.length > 0) return 'required';
  if (models.preferred.length > 0) return 'preferred';
  return 'unset';
}

export function hasModels(models: CriteriaModels): boolean {
  return models.required.length > 0 || models.preferred.length > 0 || models.excluded.length > 0;
}

/**
 * Название модели для экрана: «VW Golf 8».
 *
 * Собирается из канонических значений справочника, через i18n не переводится —
 * как и марка: это имя из каталога, а не код.
 */
export function modelLabel(ref: ModelRef): string {
  return [ref.make, ref.model, ref.generation].filter(Boolean).join(' ');
}

/**
 * Пожелания — всегда мягкий признак.
 *
 * Даже `critical` не делает пожелание обязательным условием: «главное чтобы
 * безопасная» взвешивает автомобили, а не отсеивает их (MATCHING §3.5).
 */
export function wishesStrength(wishes: readonly CriteriaWish[]): CriterionStrength {
  return wishes.length > 0 ? 'preferred' : 'unset';
}

/** Порядок пожеланий на экране: сначала названное главным. */
const WISH_STRENGTH_ORDER = { critical: 0, important: 1, mention: 2 } as const;

export function orderedWishes(wishes: readonly CriteriaWish[]): CriteriaWish[] {
  return [...wishes].sort(
    (left, right) => WISH_STRENGTH_ORDER[left.strength] - WISH_STRENGTH_ORDER[right.strength],
  );
}

// --- Демоция требований (KARL_DIALOG_RFC §6.4) --------------------------------------------

/**
 * Демоции по признакам оснащения: код признака → доля каталога с данными.
 *
 * Показываются рядом с самим требованием, а не отдельным блоком внизу: строка
 * «ISOFIX обязателен» без пометки противоречит выдаче, в которой есть машины
 * без сведений об ISOFIX.
 */
export function demotedFeatureCoverage(demotions: readonly Demotion[]): Map<string, number> {
  return new Map(
    demotions
      .filter((item) => item.path === 'features.required')
      .map((item) => [item.value, item.coverage]),
  );
}

/** Демоции по числовым порогам: имя поля → доля каталога с данными. */
export function demotedThresholdCoverage(demotions: readonly Demotion[]): Map<string, number> {
  return new Map(
    demotions
      .filter((item) => item.path.startsWith('thresholds.'))
      .map((item) => [item.value, item.coverage]),
  );
}

/** Доля каталога в процентах для текста объяснения: 0.07 → «7». */
export function coveragePercent(coverage: number): number {
  return Math.round(coverage * 100);
}

// --- Обязательные условия (FE-390) --------------------------------------------------------

/**
 * Ключи разделов, которые могут быть hard-критериями (MATCHING §3.5).
 *
 * Список закрыт и повторяет таблицу жёсткости: обязательным становится только
 * то, у чего есть сопоставимое поле объявления. Сценарий использования,
 * приоритеты, годовой пробег и способ покупки сюда не входят и не войдут —
 * это способ взвесить автомобили, а не отсеять их.
 */
export type HardCriterionKey =
  | 'budget'
  | 'bodyTypes'
  | 'maxAge'
  | 'fuel'
  | 'transmission'
  | 'drivetrain'
  | 'features'
  | 'thresholds'
  | 'makesRequired'
  | 'makesExcluded'
  | 'modelsRequired'
  | 'modelsExcluded'
  | 'geography';

/**
 * Все обязательные условия запроса — блок «Обязательные условия» (FE-390).
 *
 * Пользователь обязан видеть **весь** список того, что отсеивает автомобили, а
 * не встречать его по одной строке в разных разделах: именно из этих правил
 * складывается пустая выдача. Порядок повторяет порядок hard-правил
 * (MATCHING §5), чтобы список читался вместе с воронкой исключений.
 */
export function hardCriteria(payload: CriteriaPayload): HardCriterionKey[] {
  const keys: HardCriterionKey[] = [];
  if (payload.budget.listing_budget_cents !== null) keys.push('budget');
  if (payload.body_types.mode === 'required' && payload.body_types.values.length > 0) {
    keys.push('bodyTypes');
  }
  if (payload.max_age.years !== null) keys.push('maxAge');
  if (payload.fuel.allowed.length > 0 || payload.fuel.excluded.length > 0) keys.push('fuel');
  if (payload.transmission.mode === 'required') keys.push('transmission');
  if (payload.drivetrain.mode === 'required') keys.push('drivetrain');
  if (payload.features.required.length > 0) keys.push('features');
  if (thresholdEntries(payload.thresholds).length > 0) keys.push('thresholds');
  if (payload.makes.excluded.length > 0) keys.push('makesExcluded');
  if (payload.makes.required.length > 0) keys.push('makesRequired');
  // Модели идут сразу за марками — тем же порядком, что hard-правила
  // `MODEL_EXCLUDED` и `MODEL_REQUIRED` в воронке (KARL_DIALOG_RFC §8.1).
  if (payload.models.excluded.length > 0) keys.push('modelsExcluded');
  if (payload.models.required.length > 0) keys.push('modelsRequired');
  if (payload.geography.mode === 'required') keys.push('geography');
  return keys;
}

// --- Бюджет -----------------------------------------------------------------------------

/**
 * Порог, выше которого сумма перестаёт быть бюджетом.
 *
 * Когда пользователь говорит «бюджет не ограничен», модель подставляет
 * заведомо большое число — иначе `budget.total_cents` пришлось бы оставить
 * пустым, и hard-фильтр по цене исчез бы вместе с ним. Backend хранит это
 * число как есть (оно и работает как верхняя граница), а интерфейсу показывать
 * «10 000 000 €» бессмысленно: пользователь такой суммы не называл.
 *
 * Миллион евро выбран как заведомо недостижимая цена автомобиля в каталоге:
 * ниже порога любая сумма — осмысленный бюджет.
 */
export const BUDGET_UNLIMITED_FROM_CENTS = 100_000_000;

export function isUnlimitedBudget(cents: number | null): boolean {
  return cents !== null && cents >= BUDGET_UNLIMITED_FROM_CENTS;
}

export interface BudgetTolerance {
  /** Бюджет на объявление: `total − reserve` (MVP_PRD §10). */
  baseCents: number;
  /** Верхняя допустимая цена с учётом допуска. */
  maxCents: number;
  /** Цитата пользователя — без неё допуск не существует. */
  sourceText: string | null;
}

/**
 * Допуск по бюджету: «до 30 000 €, допустимо до 32 100 €» (MVP_PRD §11).
 *
 * `null` — допуска нет, и никакой дополнительной строки показывать нельзя:
 * система никогда не расширяет бюджет сама.
 */
export function budgetTolerance(budget: CriteriaBudget): BudgetTolerance | null {
  const base = budget.listing_budget_cents;
  // У неограниченного бюджета допуск лишён смысла: превышать нечего.
  if (base === null || budget.tolerance_ratio <= 1 || isUnlimitedBudget(base)) return null;
  return {
    baseCents: base,
    maxCents: Math.round(base * budget.tolerance_ratio),
    sourceText: budget.tolerance_source_text,
  };
}

// --- Пороги -----------------------------------------------------------------------------

export type ThresholdKey = keyof CriteriaThresholds;

export interface ThresholdEntry {
  key: ThresholdKey;
  value: number;
}

const THRESHOLD_ORDER: readonly ThresholdKey[] = [
  'seats_min',
  'trunk_volume_l_min',
  'mileage_km_max',
  'power_kw_min',
];

/**
 * Заданные числовые пороги. Все они — hard-критерии и исключают автомобиль с
 * неизвестным значением (MVP_PRD §10), поэтому показываются отдельным разделом.
 */
export function thresholdEntries(thresholds: CriteriaThresholds): ThresholdEntry[] {
  return THRESHOLD_ORDER.flatMap((key) => {
    const value = thresholds[key];
    return value === null ? [] : [{ key, value }];
  });
}

// --- Акценты ----------------------------------------------------------------------------

/**
 * Строки блока «Что для вас важнее».
 *
 * Блок показывается, только если хотя бы один компонент отличается от `normal`:
 * без акцентов действуют базовые веса, и перечислять их пользователю незачем.
 */
export function accentedWeights(weights: readonly WeightPreview[]): WeightPreview[] {
  return weights.filter((item) => item.level !== NEUTRAL_LEVEL);
}

export function hasAccents(weights: readonly WeightPreview[]): boolean {
  return weights.some((item) => item.level !== NEUTRAL_LEVEL);
}

/** Акцент повышает или понижает вес — от этого зависит иконка и подпись. */
export function emphasisDirection(level: EmphasisLevel): 'up' | 'down' | 'neutral' {
  if (level === 'critical' || level === 'elevated') return 'up';
  if (level === 'reduced' || level === 'minimal') return 'down';
  return 'neutral';
}

// --- Форматирование ---------------------------------------------------------------------

/**
 * Деньги из целых центов. `float` в хранении и расчётах запрещён
 * (MVP_TECH_RFC §6) — деление нужно только для показа готового числа.
 */
export function formatCents(cents: number, locale: string, currency = 'EUR'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export function formatKilometres(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Параметры вопроса для i18n.
 *
 * Backend передаёт деньги в центах (`budget_cents`), а в тексте нужна валюта
 * локали. Ключ `<name>_cents` дополняется отформатированным `<name>`; исходное
 * значение остаётся на месте, чтобы ключ мог использовать и число. Сумма выше
 * порога «не ограничен» подставляется словами, а не числом.
 */
export function questionParams(
  params: Record<string, unknown>,
  locale: string,
  unlimitedBudgetLabel: string,
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...params };
  for (const [key, value] of Object.entries(params)) {
    if (key.endsWith('_cents') && typeof value === 'number') {
      result[key.slice(0, -'_cents'.length)] = isUnlimitedBudget(value)
        ? unlimitedBudgetLabel
        : formatCents(value, locale);
    }
  }
  return result;
}
